import { ChangeDetectorRef, Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output } from '@angular/core';
import * as moment from 'moment';
import { FormControl, FormGroup } from '@angular/forms';
import { combineLatest, Subject } from 'rxjs';
import { debounceTime, map, take, takeUntil } from 'rxjs/operators';
import { FilterValue } from '../../../../app/interfaces/base/filter-value';
import { ApiService } from '../../../../app/services/base/api.service';
import { MatCalendarHeaderWithPresetsComponent } from '../../calendar/presets/presets.component';

@Component({
  selector: 'fury-table-filter-date-range',
  templateUrl: './table-filter-date-range.component.html',
  styleUrls: ['./table-filter-date-range.component.scss'],
})
export class TableFilterDateRangeComponent implements OnInit, OnDestroy {

  public isFiltered: boolean = false;

  public matCalendarHeaderComponent = MatCalendarHeaderWithPresetsComponent;

  public form: FormGroup = new FormGroup({
    start_old: new FormControl<Date | null>(null),
    start: new FormControl<Date | null>(null),
    end_old: new FormControl<Date | null>(null),
    end: new FormControl<Date | null>(null)
  });

  @Input() name: string = null;

  @Input() customFiltering: boolean = false;
  @Output() clearFilter: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() filterChange: EventEmitter<string> = new EventEmitter<string>();

  private destroy$: Subject<null> = new Subject<null>();

  constructor(private api: ApiService,
              private cd: ChangeDetectorRef) {
  }

  @HostListener('keydown.space', ['$event'])
  @HostListener('click', ['$event']) onClick(e: Event): void {
    e.stopPropagation();
  }

  ngOnInit(): void {
    combineLatest([
      this.form.controls.start.valueChanges,
      this.form.controls.end.valueChanges
    ]).pipe(
      takeUntil(this.destroy$),
      debounceTime(200),
      map(([a$, b$]) => ({
        start: a$,
        end: b$
      }))).subscribe((data): void => {
      if (data.start && data.end) {
        this.filterChanged();
      }
    });

    this.api.preSetFilters.pipe(
      take(1),
      takeUntil(this.destroy$)
    ).subscribe((filters: FilterValue[]): void => {
      filters.forEach((filter: FilterValue): void => {
        if (this.name === filter.name) {
          const date: string[] = (<string>filter.value).split('!');
          this.form.controls.start.setValue(new Date(date[0]), {emitEvent: false});
          this.form.controls.end.setValue(new Date(date[1]), {emitEvent: false});
          this.cd.detectChanges();

          const controls = this.form.controls;
          this.isFiltered = !!(controls.start.value + controls.end.value);
        }
      });
    });

    this.api.filterCleared.pipe(takeUntil(this.destroy$)).subscribe((): void => this.clearFilterButton());
    this.api.clearAllFilters.pipe(takeUntil(this.destroy$)).subscribe((): void => this.clearFilterButton(false));
  }

  filterChanged(): void {
    const controls = this.form.controls;
    this.isFiltered = !!(controls.start.value + controls.end.value);

    if (this.customFiltering) {
      this.filterChange.emit(
        moment(controls.start.value).set({hour: 0, minute: 0, second: 0}).toISOString() +
        '!' +
        moment(controls.end.value).set({hour: 23, minute: 59, second: 59}).toISOString());
    } else {
      this.api.setFilter(
        this.name,
        moment(controls.start.value).set({hour: 0, minute: 0, second: 0}).toISOString() +
        '!' +
        moment(controls.end.value).set({hour: 23, minute: 59, second: 59}).toISOString());
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  clearFilterButton(emitEvent: boolean = true): void {
    const controls = this.form.controls;
    controls.start.setValue(null);
    controls.end.setValue(null);
    this.form.reset();

    this.isFiltered = false;

    if (emitEvent) {
      if (this.customFiltering) {
        this.clearFilter.next();
      } else {
        this.api.clearFilter(this.name);
      }
    }
  }
}
